import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TableRow,
  Grid,
  Button,
  Box,
  TableCell,
  MenuItem,
  FormControl,
  Select,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import { useUser } from '../../context/UserContext';
import {
  GetDemographicInfo,
  GetLOBByContractID,
  GetTrainingCertsByProducerID,
  GetAppointmentInformation,
} from '../../api/regedint';
import { StrikeThroughHeader } from '../../components/Headers/strikethrough-header/strikethrough-header';
import ProducerIDsDropdown from '../../components/Global/ProducerIDsDropdown/ProducerIDsDropdown';
import Demographic from './Demographic';
import Training from './Training';
import AppointmentsTable from './Appointments';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';

import AgentDevice from './AgentDevice';
import ChangeRequests from './ChangeRequests';
import Notifications from './Subpages/Notifications/Notifications';
import './MyProfile.scss';

export default function PagesNavBar({ prevLocation }) {
  const navigate = useNavigate();
  const user = useUser();
  const params = useParams();
  const [producerID, setProducerID] = useState(GetIMOProducerID());
  const prevMyIMOLocation = useRef(prevLocation);

  const [myIMOView, setMyIMOView] = useState(GetIMOProducerID() !== '' ? true : false);
  let location = useLocation();

  const [contractID, setContractID] = useState(myIMOView ? producerID : user.producerID);
  const [canSellAnnuities, setCanSellAnnuities] = useState(null);

  const showAgentDevice =
    user.userType === 'Agent' || (myIMOView && user.hasProducerID(producerID));

  const [category, setCategory] = useState('Demographic');

  const mobileSelectStyle = { margin: '0 0.5em' };
  const [isContractIDsDropdownVisible, setIsContractIDsDropdownVisible] = useState(false);

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const [tab, setTab] = useState(0);
  const tabRoutes = [
    '/MyProfile/Demographic',
    '/MyProfile/Training',
    '/MyProfile/Appointments',
    ...(showAgentDevice ? ['/MyProfile/AgentDevice'] : []),
    '/MyProfile/ChangeRequests',
    '/MyProfile/Notifications',
  ];

  const handleTabChange = (_event, newValue) => {
    setCategory(newValue);
    navigate(myIMOView ? tabRoutes[newValue] + '/' + contractID : tabRoutes[newValue]);
  };

  const handleChange = (event) => {
    setCategory(event.target.value);
  };

  const mobile = useMediaQuery('(max-width:750px)');

  const getDemographicInfo = useQuery({
    queryKey: ['GetDemographicInfo', contractID],
    queryFn: () => GetDemographicInfo(contractID)(),
    enabled: user != null && user.isHO === false,
  });

  const [apptColumns, setApptColumns] = useState([]);
  const [apptRowData, setApptRowData] = useState([]);

  const getAppointmentInformation = useQuery({
    queryKey: [
      'GetAppointmentInformation',
      getDemographicInfo.data != null ? getDemographicInfo.data.nationalProducerNumber : user.npnID,
    ],
    queryFn: () =>
      GetAppointmentInformation({
        AccountId: '0',
        AgentId:
          getDemographicInfo.data != null
            ? getDemographicInfo.data.nationalProducerNumber
            : user.npnID,
      })(),
    enabled:
      user != null &&
      user.isHO === false &&
      getDemographicInfo.data != null &&
      getDemographicInfo.data.nationalProducerNumber != null,
  });

  useEffect(() => {
    if (getAppointmentInformation.isSuccess && getAppointmentInformation.data) {
      if (getAppointmentInformation.data.appointments != null) {
        var columnsArray = [
          { field: 'stateCode', headerName: 'State', flex: 1.5, align: 'left' },
          { field: 'renewalDate', headerName: 'Renewal Date', flex: 1.5, align: 'left' },
        ];

        setApptColumns(columnsArray);

        var rData = getAppointmentInformation.data.appointments?.map((appt, index) => ({
          id: `row-${index}`,
          stateCode: appt.stateCode,
          statusDescription: appt.statusDescription,
          renewalDate: appt.renewalDate,
          rowIndex: index,
        }));

        setApptRowData(rData);
      }
    }
  }, [getAppointmentInformation.isSuccess, getAppointmentInformation.data]);

  function GetIMOProducerID() {
    var wildCard = params['*'];
    var routeArray = wildCard.split('/');

    if (routeArray.length > 1) {
      return routeArray[1];
    }

    return '';
  }

  useEffect(() => {
    var wildCard = params['*'];
    var routeArray = wildCard.split('/');

    if (routeArray.length > 0) {
      setCategory(routeArray[0]);

      const tabIndex = tabRoutes.findIndex((path) =>
        ('/MyProfile/' + routeArray[0]).startsWith(path)
      );
      if (tabIndex !== -1 && tabIndex !== tab) {
        setTab(tabIndex);
      }
    }

    if (routeArray.length > 1) {
      setProducerID(routeArray[1]);
      setMyIMOView(true);
    }
  }, [params]);

  useEffect(() => {
    if (location.pathname === '/MyProfile') {
      navigate(tabRoutes[0]);
    }
  }, []);

  const getLOBByContractID = useQuery({
    queryKey: ['GetLOBByContractID', contractID],
    queryFn: () => GetLOBByContractID(contractID)(),
    enabled: user != null && user.isHO === false,
  });

  useEffect(() => {
    if (getLOBByContractID.isSuccess && getLOBByContractID.data) {
      let sellsAnnuities = false;

      for (var i = 0; i < getLOBByContractID.data.length; i++) {
        var product = getLOBByContractID.data[i];

        if (product.agentSiteMapping === 'SPDA' || product.agentSiteMapping === 'SPIA') {
          sellsAnnuities = true;
          break;
        }
      }

      setCanSellAnnuities(sellsAnnuities);
    }
  }, [getLOBByContractID.isSuccess, getLOBByContractID.data]);

  const getTrainingCerts = useQuery({
    queryKey: ['GetTrainingCertsByProducerID', contractID],
    queryFn: () =>
      GetTrainingCertsByProducerID({
        ProducerID: contractID,
        SellsAnnuities: canSellAnnuities,
      })(),
    enabled:
      user != null &&
      getLOBByContractID.data != null &&
      canSellAnnuities != null &&
      user.isHO === false,
  });

  function HasAgentData() {
    if (
      getDemographicInfo.data !== null &&
      getAppointmentInformation.data !== null &&
      getLOBByContractID.data !== null &&
      getTrainingCerts.data != null
    )
      return true;
    else return false;
  }

  function IsLoadingData() {
    if (
      getDemographicInfo.isPending ||
      getDemographicInfo.isFetching ||
      getAppointmentInformation.isPending ||
      getAppointmentInformation.isFetching ||
      getLOBByContractID.isPending ||
      getLOBByContractID.isFetching ||
      getTrainingCerts.isPending ||
      getTrainingCerts.isFetching
    )
      return true;

    return false;
  }

  function DesktopSelectUpdated(props) {
    const baseTabs = ['Demographic', 'Training', 'Appointments'];
    const agentTabs = ['Change Requests', 'Notifications'];

    const tabs = [
      ...baseTabs,
      ...(showAgentDevice ? ['Agent Device'] : []),
      ...(user.userType === 'Agent' ? agentTabs : []),
    ];

    const commonTabSx = {
      fontSize: { xs: '.7rem', md: 'default' },
      wrap: 'none',
      minWidth: { xs: 120, md: 160 },
    };

    return (
      <Tabs
        sx={{
          width: { xs: '100%', md: 'auto' },
          maxWidth: '100%',
        }}
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((label, index) => (
          <Tab key={index} sx={commonTabSx} label={label} {...a11yProps(index)} />
        ))}
      </Tabs>
    );
  }

  const tabComponents = {
    Demographic: () => (
      <Demographic demographicInfo={getDemographicInfo.data} contractID={contractID} />
    ),
    Training: () => (
      <Training trainingInfo={getTrainingCerts.data} demographicInfo={getDemographicInfo.data} />
    ),
    Appointments: () => (
      <AppointmentsTable
        npnID={getDemographicInfo.data.nationalProducerNumber}
        appointmentInfo={getAppointmentInformation.data}
        apptRowData={apptRowData}
        apptColumns={apptColumns}
      />
    ),
    AgentDevice: () => (
      <AgentDevice
        agentInfo={getDemographicInfo.data}
        user={user}
        canSubmit={user.userType === 'Agent' || (myIMOView && user.hasProducerID(producerID))}
      />
    ),
    ChangeRequests: () => <ChangeRequests user={user} />,
    Notifications: () => <Notifications user={user} />,
  };

  const ActiveComponent = tabComponents[category];

  if (user.isHO === true) {
    return (
      <div>
        <StrikeThroughHeader>My Profile</StrikeThroughHeader>

        <Grid size={12} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 'x-large' }}>No Agent Data Listed</div>
        </Grid>
      </div>
    );
  }

  return (
    <div>
      <StrikeThroughHeader>My Profile</StrikeThroughHeader>
      {myIMOView && (
        <Box sx={{ padding: '1rem 0', width: 'fit-content' }}>
          <Link to={prevMyIMOLocation.current ? prevMyIMOLocation.current : '/MyIMO'}>
            <Button sx={{ display: 'flex', gap: '.25rem', textTransform: 'none' }} variant="text">
              <ArrowBackIcon /> Back to MyIMO
            </Button>
          </Link>
        </Box>
      )}

      <Grid container spacing={2} sx={{ margin: '0 0' }}>
        {mobile ? (
          <MobileSelect
            user={user}
            location={location}
            category={category}
            handleChange={handleChange}
            isContractIDsDropdownVisible={isContractIDsDropdownVisible}
            myIMOView={myIMOView}
            contractID={contractID}
          />
        ) : (
          <DesktopSelectUpdated />
        )}
        {myIMOView === false && (
          <>
            <Grid
              size={isContractIDsDropdownVisible ? 6 : 0}
              style={{ textAlign: mobile ? 'left' : 'right' }}
            >
              <ProducerIDsDropdown
                setIsVisible={setIsContractIDsDropdownVisible}
                setProducerID={(id) => {
                  setContractID(id);
                }}
                sx={{ width: 'fit-content', float: 'right' }}
                formControlStyle={mobileSelectStyle}
                variant="standard"
              />
            </Grid>
            {/*<Grid xs={2} style={{ textAlign: 'right' }}>
			<Grid container spacing={2} sx={{ margin: '0 0' }}>
				{mobile ? <MobileSelect user={user} location={location} category={category} handleChange={handleChange} isContractIDsDropdownVisible={isContractIDsDropdownVisible} myIMOView={myIMOView} contractID={contractID} showAgentDevice={showAgentDevice} /> : <DesktopSelectUpdated />}
				{myIMOView === false &&
					<>
						<Grid xs={isContractIDsDropdownVisible ? 6 : 0} style={{ textAlign: mobile ? 'left' : 'right' }}>
							<ProducerIDsDropdown setIsVisible={setIsContractIDsDropdownVisible} setProducerID={(id) => { setContractID(id); }} sx={{ width: 'fit-content', float: 'right' }} formControlStyle={mobileSelectStyle} variant="standard" />
						</Grid>
						{/*<Grid xs={2} style={{ textAlign: 'right' }}>
                            <WellsFargoSSOLink demographicInfo={getDemographicInfo.data}
                                npnID={user.npnID} />
                        </Grid>*/}
          </>
        )}
      </Grid>

      {IsLoadingData() && (
        <CircularProgress size={64} style={{ marginLeft: '50%', marginTop: '15%' }} />
      )}

      {HasAgentData() && !IsLoadingData() && ActiveComponent && <ActiveComponent />}
    </div>
  );
}

function MobileSelect(props) {
  const menuItems = [
    { value: 'Demographic', label: 'Demographic', path: '/MyProfile/Demographic' },
    { value: 'Training', label: 'Training', path: '/MyProfile/Training' },
    { value: 'Appointments', label: 'Appointments', path: '/MyProfile/Appointments' },
    ...(props.showAgentDevice
      ? [{ value: 'AgentDevice', label: 'Agent Device', path: '/MyProfile/AgentDevice' }]
      : []),
    ...(props.user.userType === 'Agent'
      ? [
          { value: 'ChangeRequests', label: 'Change Requests', path: '/MyProfile/ChangeRequests' },
          { value: 'Notifications', label: 'Notifications', path: '/MyProfile/Notifications' },
        ]
      : []),
  ];

  const handleMenuItemClick = (item) => {
    const path =
      props.myIMOView && item.value !== 'ChangeRequests' && item.value !== 'Notifications'
        ? `${item.path}/${props.contractID}`
        : item.path;

    // Use navigate if available, otherwise use Link
    if (props.navigate) {
      props.navigate(path);
    }
  };

  return (
    <Grid
      xs={props.isContractIDsDropdownVisible ? 6 : 12}
      sx={{ textAlign: props.isContractIDsDropdownVisible ? 'right' : 'left' }}
    >
      <FormControl sx={{ width: 'auto', margin: '0 0.5em' }}>
        <Select
          labelId="mobile-profile-select-label"
          id="mobile-profile-select"
          value={props.category}
          onChange={props.handleChange}
          MenuProps={{
            MenuListProps: {
              sx: {
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              },
            },
          }}
        >
          {menuItems.map((item) => (
            <MenuItem key={item.value} value={item.value} selected={props.category === item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );
}

export const InfoLabel = ({ label, data, style, fullWidth }) => {
  return (
    <Grid size={{ xs: 12, sm: fullWidth ? 12 : 6 }}>
      <Grid container>
        <Grid size={fullWidth ? 2 : 4}>
          <div className="infoLabel">{label}</div>
        </Grid>
        <Grid size={fullWidth ? 10 : 8} sx={style}>
          {data != null && data.map((dt, i) => <div key={dt + '-' + i}>{dt}</div>)}
        </Grid>
      </Grid>
    </Grid>
  );
};

export const InfoLabelInline = ({ data }) => {
  return (
    <>
      {data != null &&
        data.map((dt, i) => (
          <Grid key={dt.value + '-' + i} size={dt.size}>
            {dt.value}
          </Grid>
        ))}
    </>
  );
};

export const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));
